import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Pleasanton", cities: "Downtown, Hacienda Business Park, Birdland, Ruby Hill, Vintage Hills" },
    { zone: "Dublin", cities: "Emerald Glen, West Dublin, Fallon Village, Jordan Ranch" },
    { zone: "Livermore", cities: "Downtown Livermore, Springtown, Jensen Tract, South Livermore" },
    { zone: "San Ramon", cities: "Bishop Ranch, Crow Canyon, Dougherty Valley, Norris Canyon" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Tri-Valley</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Pleasanton, Dublin, Livermore, San Ramon y más.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Pleasanton</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const PleasantonSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "pleasanton", name: "Pleasanton", type: "city", regionSlug: "alameda" }}>
      <SEO
        title="Limpieza del Hogar en Pleasanton, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Pleasanton, Dublin, Livermore y San Ramon. Precios planos sin sorpresas, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar pleasanton, servicio de limpieza pleasanton ca, empleada doméstica pleasanton, maid service pleasanton español, limpieza de casas tri-valley, limpieza dublin ca, limpieza livermore, limpieza san ramon"
        url="https://uplevelcleaning.com/es/pleasanton"
        locationName="Pleasanton"
        regionSlug="alameda"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Condado de Alameda", url: "/es/alameda" },
          { name: "Pleasanton", url: "/es/pleasanton" }
        ]}
        reviews={[
          { author: "Valentina R.", rating: 5, reviewBody: "Vivo en Pleasanton y llevaba mucho tiempo buscando un buen servicio de limpieza. Uplevel Cleaning es exactamente lo que necesitaba. Mi hogar quedó impecable y el precio fue muy justo.", datePublished: "2026-03-22" },
          { author: "Carlos G.", rating: 5, reviewBody: "Contraté la limpieza profunda para nuestra casa en Dublin. El equipo llegó puntual, fue muy detallista y profesional. ¡Sin duda los volveré a llamar!", datePublished: "2026-02-18" },
          { author: "Patricia N.", rating: 5, reviewBody: "Llevo 3 meses con el servicio quincenal en Livermore. Siempre puntuales, siempre minuciosos. El mejor servicio de limpieza del Tri-Valley.", datePublished: "2026-01-25" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Pleasanton" regionSlug="alameda" />

          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Pleasanton · Dublin · Livermore",
                "San Ramon · Tri-Valley",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Pleasanton" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="oakland" />
          <Guarantee />
          <SpanishFAQ cityName="Pleasanton" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Pleasanton" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default PleasantonSpanishPage;
